import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { useBooking } from "./BookingContext";
import { RingBadge } from "./RingBadge";

const LINES = ["Intuitive psychic readings", "and sacred moon rituals,", "to guide you closer", "to the God within."];
const ease = [0.16, 1, 0.3, 1];

const Line = ({ text, cls = "", i }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span className={`block ${cls}`} initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 1.3, delay: 0.25 + i * 0.13, ease }}>
      {text}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { openBooking } = useBooking();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const mx = useMotionValue(0), my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 80, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 80, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5); my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section id="top" ref={ref} className="relative min-h-screen pt-[110px] pb-16 overflow-hidden">
      <div className="absolute -top-40 -right-40 w-[620px] h-[620px] rounded-full bg-[#E8D8C8]/60 blur-3xl" aria-hidden />
      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <div className="lg:col-span-7">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1, duration: 1 }} className="text-xs uppercase tracking-[0.3em] text-[#A8613C] font-semibold mb-8">
            Shaya - Worthy - Deserving - Gift
          </motion.p>
          <h1 data-testid="hero-title" className="font-display font-light text-[#2D2A26] leading-[1.02] tracking-[-0.01em] text-[clamp(2.4rem,4.4vw,4.25rem)]">
            {LINES.map((t, i) => <Line key={i} i={i} text={t} cls={i === 3 ? "italic text-[#A8613C]" : ""} />)}
          </h1>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 1, ease }} className="mt-10 max-w-lg">
            <p className="text-base md:text-lg text-[#5E584F] leading-relaxed">
              Offered online, with gratitude, humility and reverence.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <button data-testid="hero-book-cta" onClick={() => openBooking()} className="btn-pill bg-[#A8613C] text-white hover:bg-[#2D2A26] px-7 py-4 text-sm">Book a session</button>
              <a data-testid="hero-explore-cta" href="/#offerings" className="btn-pill border border-[#2D2A26]/30 text-[#2D2A26] hover:border-[#2D2A26] px-7 py-4 text-sm">
                Explore offerings <ArrowDownRight size={16} />
              </a>
            </div>
          </motion.div>
        </div>
        <div className="lg:col-span-5 relative" style={{ perspective: 1200 }} onMouseMove={onMove} onMouseLeave={() => { mx.set(0); my.set(0); }}>
          <motion.div initial={{ clipPath: "inset(100% 0% 0% 0% round 999px 999px 24px 24px)" }} animate={{ clipPath: "inset(0% 0% 0% 0% round 999px 999px 24px 24px)" }} transition={{ duration: 1.6, delay: 0.4, ease }}
            style={{ rotateX: rx, rotateY: ry }} className="relative mx-auto w-[82%] sm:w-[70%] lg:w-full aspect-[3/4] overflow-hidden rounded-t-full rounded-b-3xl shadow-[0_40px_80px_-30px_rgba(45,42,38,0.45)]">
            <motion.img data-testid="hero-image" src="/img/diana.webp" alt="Diana, founder of Shaya Spiritual Practice" style={{ y: imgY, scale: 1.18 }} className="w-full h-full object-cover object-[50%_15%]" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_30%,transparent_35%,rgba(45,42,38,0.35)_100%)]" />
          </motion.div>
          <div className="absolute -left-2 sm:left-0 lg:-left-16 bottom-6 lg:bottom-16"><RingBadge /></div>
        </div>
      </div>
    </section>
  );
};
